import React, {Component, createRef} from 'react'
import propTypes from 'prop-types'
import {get, has, isNil} from 'lodash'
import TextField from '@material-ui/core/TextField'
import Timer from 'helpers/timer'
import {formatRoundedNumber} from "helpers/financial"
import './numberField.scss'

const classPrefix = 'numberField'

class NumberField extends Component {
  ref = {
    input: createRef(),
    container: createRef(),
    increaseButton: createRef(),
    decreaseButton: createRef(),
  }

  constructor(props, context) {
    super(props, context)
    this.state = {
      value: this.getAsString(this.props.value), // formatted string
      rawValue: this.props.value,
    }
    this.timer = new Timer(500, this.commit)
  }

  // Returns a number
  getAsNumber = (value) => {
    if (value == null) return null
    return parseFloat(this.getAsString(value))
  }

  // Returns a string
  getAsString = (value) => {
    const {options} = this.props
    if (value == null) return ''

    let ret = parseFloat(value)
    if (isNaN(ret)) ret = 0

    const min = parseFloat(get(options, 'min'))
    if (!isNil(min) && ret < min) ret = min

    const max = parseFloat(get(options, 'max'))
    if (!isNil(max) && ret > max) ret = max

    const accuracy = parseFloat(get(options, 'accuracy', 0))
    return formatRoundedNumber(ret, accuracy)
  }

  UNSAFE_componentWillReceiveProps = (nextProps) => {
    if (nextProps.value !== this.props.value) {
      this.setState({
        value: this.getAsString(nextProps.value),
        rawValue: nextProps.value,
      })  // formatted string
    }
  }

  onLabelClick = (e) => {
    this.focus()
    e.stopPropagation()
  }

  focus = (forEdit) => {
    this.ref.input.current.focus()
    if (forEdit) this.ref.input.current.select()
  }

  isFocusStayingWithin = (event) => {
    return event.relatedTarget && (
      this.ref.container.current?.contains(event.relatedTarget)
      || this.ref.increaseButton.current?.contains(event.relatedTarget)
      || this.ref.decreaseButton.current?.contains(event.relatedTarget)
    )
  }

  onBlur = (event) => {
    if (this.isFocusStayingWithin(event)) {
      return // we don't want to lose focus when clicking on increase/decrease buttons
    }

    const {onBlur, preventClear} = this.props
    const {value} = this.state

    // prevents entering of a number when 'tabbing through' an empty field
    if (event.target.value === '' && value === '') {
      if (preventClear) {
        const {defaultValue} = this.props
        const fallback = isNil(defaultValue) ? this.props.value : defaultValue
        this.setState({
          value: this.getAsString(fallback),
          rawValue: fallback,
        }, () => this.commit(this.state.rawValue))
      }
      if (onBlur) onBlur(event)
    } else {
      this.timer.cancel()
      this.setState({
        value: this.getAsString(event.target.value),
        rawValue: event.target.value,
      }, () => {
        this.commit(this.state.rawValue)
        if (onBlur) onBlur(event)
      })
    }
  }

  onChange = (event) => {
    // Don't update on enter - this is handled by keydown below. Will block the onBlur updateState function
    if (event.keyCode !== 13) {
      this.setState({value: event.target.value, rawValue: event.target.value})  // no change to input at this stage
    }
  }

  onKeyDown = (event) => {
    switch (event.keyCode) {
      case 13:  // ENTER
        this.timer.cancel()
        this.commit(event.target.value)
        break

      case 27:  // ESC
        this.cancel()
        break

      case 38:  // UP
        this.stepUp()
        event.preventDefault()
        break

      case 40:  // DOWN
        this.stepDown()
        event.preventDefault()
        break
    }
    if (this.props.onKeyDown) this.props.onKeyDown(event)
  }

  cancel = () => {
    this.setState({value: this.getAsString(this.props.value), rawValue: this.props.value}) // formatted string
  }

  debounce = (value) => {
    const {debounce} = this.props

    this.setState({
      value: this.getAsString(value), // formatted string
      rawValue: value,
    }, () => {
      if (debounce) {
        this.timer.fire()
      } else {
        this.commit(value)
      }
    })
  }

  commit = (value) => {
    value = value || this.state.value
    this.props.onChange(this.getAsNumber(value))
  }

  stepUp = () => {
    const {options} = this.props
    let value = parseFloat(this.state.value)
    if (isNaN(value)) value = 0

    const step = parseFloat(get(options, 'step', 1))
    value -= value % step
    value = value + step

    this.ref.input.current.focus() // don't lose focus on the input on clicking the button
    this.debounce(value)
  }

  stepDown = () => {
    const {options} = this.props
    let value = parseFloat(this.state.value)
    if (isNaN(value)) value = 0

    const step = parseFloat(get(options, 'step', 1))
    let rem = value % step
    if (rem === 0) rem = step
    value -= step

    this.ref.input.current.focus() // don't lose focus on the input on clicking the button
    this.debounce(value)
  }

  render() {
    const {
      fieldKey,
      className,
      options,
      fullWidth,
      showBorder,
      disabled,
      showStepper,
      style,
      width,
      textFieldStyle,
      placeholder,
      readOnly,
      onFocus,
    } = this.props
    const {value} = this.state

    let containerClassName = (showBorder) ? classPrefix + '-container' : classPrefix + '-container noBorder'
    if (disabled) {
      containerClassName = containerClassName + ' disabled'
    }

    return (
      <div className={containerClassName} style={style} ref={this.ref.container}>
        {options?.prepend && (
          <div
            onClick={this.onLabelClick}
            className={`${classPrefix}-prepend`}
            style={options.prependStyle}
          >
            {options.prepend}
          </div>
        )}
        <TextField
          id={fieldKey}
          name="NumberField"
          type="number"
          placeholder={placeholder}
          inputRef={this.ref.input}
          className={`${classPrefix}-numberFieldInput ${className}`}
          fullWidth={fullWidth}
          onChange={this.onChange}
          onFocus={onFocus}
          onBlur={this.onBlur}
          onKeyDown={this.onKeyDown}
          value={value}
          disabled={disabled}
          InputProps={{
            disableUnderline: true,
            readOnly,
          }}
          inputProps={{
            step: has(options, 'step') ? get(options, 'step', 1) : 1,
          }}
          style={{...textFieldStyle, width}}
        />
        {options?.append && (
          <div
            onClick={this.onLabelClick}
            className={`${classPrefix}-append`}
            style={options.appendStyle}
          >
            {options.append}
          </div>
        )}
        {showStepper && has(options, 'step') && (
          <div className={`${classPrefix}-buttonGroup`}>
            <button
              ref={this.ref.decreaseButton}
              tabIndex={-1}
              disabled={disabled}
              className={`${classPrefix}-decrease`}
              type="button"
              onClick={this.stepDown}
            >
              -
            </button>
            <button
              ref={this.ref.increaseButton}
              tabIndex={-1}
              disabled={disabled}
              className={`${classPrefix}-increase`}
              type="button"
              onClick={this.stepUp}
            >
              +
            </button>
          </div>
        )}
      </div>
    )
  }
}

NumberField.propTypes = {
  fieldKey: propTypes.string,
  className: propTypes.string,
  label: propTypes.string,
  value: propTypes.oneOfType([
    propTypes.string,
    propTypes.number,
  ]),
  options: propTypes.shape({
    min: propTypes.oneOfType([
      propTypes.string,
      propTypes.number,
    ]),
    max: propTypes.oneOfType([
      propTypes.string,
      propTypes.number,
    ]),
    step: propTypes.oneOfType([
      propTypes.string,
      propTypes.number,
    ]),
    accuracy: propTypes.oneOfType([
      propTypes.string,
      propTypes.number,
    ]),
    prepend: propTypes.oneOfType([propTypes.element, propTypes.string]),
    append: propTypes.oneOfType([propTypes.element, propTypes.string]),
    prependStyle: propTypes.object,
    appendStyle: propTypes.object,
    showStepper: propTypes.bool,
  }),
  debounce: propTypes.bool,
  preventClear: propTypes.bool,
  defaultValue: propTypes.oneOfType([propTypes.string, propTypes.number]),
  showBorder: propTypes.bool,
  disabled: propTypes.bool,
  showStepper: propTypes.bool,
  placeholder: propTypes.string,
  fullWidth: propTypes.bool,
  readOnly: propTypes.bool,
  style: propTypes.object,
  width: propTypes.oneOfType([propTypes.string, propTypes.number]),
  textFieldStyle: propTypes.object,
  onChange: propTypes.func.isRequired,
  onFocus: propTypes.func,
  onBlur: propTypes.func,
  onKeyDown: propTypes.func,
}

NumberField.defaultProps = {
  fieldKey: '',
  className: '',
  showStepper: true,
  debounce: true,
  preventClear: false,
}

export default NumberField
